import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, ShoppingBag, AlertCircle, X, Truck, CreditCard } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Stripe promise
let stripePromise = null;
const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stripe/publishable-key`);
      stripePromise = loadStripe(response.data.publishable_key);
    } catch (error) {
      console.error('Error loading Stripe key:', error);
    }
  }
  return stripePromise;
};

// Stripe Card Input styling
const cardStyle = {
  style: {
    base: {
      color: '#fff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '16px',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: { color: '#ef4444' },
  },
};

// Payment Form Component
const PaymentForm = ({ amount, onPaymentSuccess, onCancel, customerInfo, bargainRequest }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      // Create payment intent (using public endpoint for unauthenticated bargain purchases)
      const { data } = await axios.post(`${API_BASE_URL}/api/public-create-payment-intent`, {
        amount: Math.round(amount * 100),
        currency: 'usd',
        metadata: {
          type: 'bargain_purchase',
          bargain_request_id: bargainRequest.id,
          customer_email: customerInfo.email,
          customer_name: customerInfo.fullName
        }
      });

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        data.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: customerInfo.fullName,
              email: customerInfo.email,
            },
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
      } else if (paymentIntent.status === 'succeeded') {
        onPaymentSuccess(paymentIntent);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-gray-700 rounded-lg p-4">
        <CardElement options={cardStyle} />
      </div>
      
      {error && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 text-sm text-red-300">
          {error}
        </div>
      )}
      
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 rounded-lg bg-gray-700 text-white font-medium hover:bg-gray-600"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 py-3 rounded-lg bg-gray-600 text-white font-semibold disabled:opacity-70 flex items-center justify-center gap-2 hover:bg-gray-500"
        >
          {processing ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            <>Pay ${amount.toFixed(2)}</>
          )}
        </button>
      </div>
      
      <p className="text-xs text-center text-gray-500">
        🔒 Secure payment powered by Stripe
      </p>
    </form>
  );
};

const BargainPurchase = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [validationError, setValidationError] = useState(null);
  
  // Checkout states
  const [step, setStep] = useState('details'); // details, checkout, payment, confirmation
  const [shippingSettings, setShippingSettings] = useState(null);
  const [storeSettings, setStoreSettings] = useState(null);
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    shippingMethod: '',
    specialInstructions: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Load Stripe
  useEffect(() => {
    getStripePromise().then(setStripePromise);
  }, []);

  // Fetch purchase details, shipping settings, and store settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch bargain purchase details (public endpoint)
        const purchaseResponse = await axios.get(`${API_BASE_URL}/api/bargain-purchase/${token}`);
        
        if (purchaseResponse.data.success) {
          setPurchaseData(purchaseResponse.data);
          // Pre-fill customer info
          const req = purchaseResponse.data.request;
          setCheckoutForm(prev => ({
            ...prev,
            fullName: req.customer_name || '',
            email: req.email || '',
            phone: req.phone || ''
          }));
        } else {
          setError('Invalid purchase link');
        }
        
        // Fetch store settings (tax, payments, etc.)
        try {
          const storeResponse = await axios.get(`${API_BASE_URL}/api/store-settings`);
          if (storeResponse.data) {
            setStoreSettings(storeResponse.data);
          }
        } catch (storeErr) {
          console.log('Using default store settings');
        }
        
        // Try to fetch ecommerce settings for shipping
        try {
          const settingsResponse = await axios.get(`${API_BASE_URL}/api/settings/public`);
          if (settingsResponse.data) {
            setShippingSettings(settingsResponse.data.shipping || { shippingType: 'flat', flatRate: 5.00 });
          }
        } catch (settingsErr) {
          console.log('Using default shipping settings');
          setShippingSettings({ shippingType: 'flat', flatRate: 5.00 });
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.detail || 'This purchase link is invalid or has expired');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    } else {
      setError('No purchase token provided');
      setLoading(false);
    }
  }, [token]);

  const calculateShipping = () => {
    if (!shippingSettings) return 0;
    
    if (shippingSettings.shippingType === 'free') return 0;
    if (shippingSettings.shippingType === 'flat') return shippingSettings.flatRate || 0;
    
    return shippingSettings.flatRate || 0;
  };

  const calculateTax = (subtotal) => {
    // Check if tax is enabled in store settings
    if (!storeSettings?.tax?.enabled) {
      return 0;
    }
    
    // Get tax rate from store settings
    let taxRate = 0;
    if (storeSettings.tax.taxType === 'custom') {
      taxRate = storeSettings.tax.customTaxRate || 0;
    } else {
      taxRate = storeSettings.tax.taxRate || 0;
    }
    
    // Convert percentage to decimal (e.g., 8.25 -> 0.0825)
    return subtotal * (taxRate / 100);
  };

  const getTaxRate = () => {
    if (!storeSettings?.tax?.enabled) return 0;
    if (storeSettings.tax.taxType === 'custom') {
      return storeSettings.tax.customTaxRate || 0;
    }
    return storeSettings.tax.taxRate || 0;
  };

  const getSubtotal = () => {
    return (purchaseData?.request?.final_price || 0) * (purchaseData?.request?.quantity || 1);
  };

  const getTotal = () => {
    const subtotal = getSubtotal();
    const shipping = calculateShipping();
    const tax = calculateTax(subtotal);
    return subtotal + shipping + tax;
  };

  const handleCheckout = () => {
    setStep('checkout');
  };

  const handleProceedToPayment = () => {
    if (!checkoutForm.fullName || !checkoutForm.email || !checkoutForm.phone) {
      setValidationError('Please fill in all required contact fields');
      return;
    }
    if (!checkoutForm.streetAddress || !checkoutForm.city || !checkoutForm.state || !checkoutForm.zipCode) {
      setValidationError('Please fill in all shipping address fields');
      return;
    }
    setValidationError(null);
    setStep('payment');
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    setSubmitting(true);
    try {
      const subtotal = getSubtotal();
      const shippingFee = calculateShipping();
      const taxAmount = calculateTax(subtotal);
      
      const response = await axios.post(`${API_BASE_URL}/api/bargain-purchase/${token}/complete`, {
        payment_intent_id: paymentIntent.id,
        shipping_address: {
          street: checkoutForm.streetAddress,
          city: checkoutForm.city,
          state: checkoutForm.state,
          zip: checkoutForm.zipCode
        },
        customer_info: {
          name: checkoutForm.fullName,
          email: checkoutForm.email,
          phone: checkoutForm.phone
        },
        special_instructions: checkoutForm.specialInstructions,
        shipping_fee: shippingFee,
        tax: taxAmount
      });
      
      setConfirmedOrder({
        orderNumber: response.data.order_number || `BRG-${Date.now().toString(36).toUpperCase()}`,
        items: [{
          name: purchaseData?.request?.product_name,
          quantity: purchaseData?.request?.quantity,
          price: purchaseData?.request?.final_price
        }],
        subtotal: subtotal,
        shipping: shippingFee,
        tax: taxAmount,
        taxRate: getTaxRate(),
        taxEnabled: storeSettings?.tax?.enabled || false,
        total: getTotal(),
        shippingAddress: {
          street: checkoutForm.streetAddress,
          city: checkoutForm.city,
          state: checkoutForm.state,
          zip: checkoutForm.zipCode
        },
        customerInfo: {
          name: checkoutForm.fullName,
          email: checkoutForm.email,
          phone: checkoutForm.phone
        }
      });
      
      setStep('confirmation');
    } catch (err) {
      console.error('Error completing order:', err);
      alert('Payment successful but order completion failed. Please contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="text-gray-300 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-10 w-10 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Link Invalid or Expired</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/order')}
            className="px-6 py-2 rounded-lg bg-gray-700 text-white font-medium hover:bg-gray-600"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const request = purchaseData?.request;

  // Confirmation state
  if (step === 'confirmation' && confirmedOrder) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 flex-shrink-0 bg-gray-800 rounded-t-2xl">
            <h3 className="text-lg font-bold text-white text-center">Order Confirmed!</h3>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="text-center">
              <h4 className="text-xl font-bold text-white mb-1">Thank You!</h4>
              <p className="text-gray-400 text-sm">Order #{confirmedOrder.orderNumber}</p>
            </div>

            {/* Order Summary */}
            <div className="bg-gray-800 rounded-lg p-4 space-y-3">
              <h5 className="text-sm font-medium text-gray-300">Order Summary</h5>
              {confirmedOrder.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-white">{item.name} x {item.quantity}</span>
                  <span className="text-gray-400">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-gray-700 pt-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-white">${confirmedOrder.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Shipping</span>
                  <span className="text-white">${confirmedOrder.shipping.toFixed(2)}</span>
                </div>
                {confirmedOrder.taxEnabled && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Tax ({confirmedOrder.taxRate}%)</span>
                    <span className="text-white">${confirmedOrder.tax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-semibold mt-2">
                  <span className="text-white">Total</span>
                  <span className="text-green-400">${confirmedOrder.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h5 className="text-sm font-medium text-gray-300 mb-2">Shipping To</h5>
              <p className="text-white text-sm">{confirmedOrder.customerInfo.name}</p>
              <p className="text-gray-400 text-sm">{confirmedOrder.shippingAddress.street}</p>
              <p className="text-gray-400 text-sm">
                {confirmedOrder.shippingAddress.city}, {confirmedOrder.shippingAddress.state} {confirmedOrder.shippingAddress.zip}
              </p>
              <p className="text-gray-400 text-sm mt-1">{confirmedOrder.customerInfo.email}</p>
              <p className="text-gray-400 text-sm">{confirmedOrder.customerInfo.phone}</p>
            </div>

            <p className="text-xs text-center text-gray-500">
              A confirmation email has been sent to {confirmedOrder.customerInfo.email}
            </p>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-700 flex-shrink-0 bg-gray-800 rounded-b-2xl">
            <button
              onClick={() => navigate('/order')}
              className="w-full py-3 rounded-lg bg-gray-700 text-white font-semibold hover:bg-gray-600"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Sticky Header */}
        <div className="px-6 py-4 flex-shrink-0 bg-gray-800 rounded-t-2xl flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Your Bargain Deal</h3>
          <button onClick={() => navigate('/order')} className="text-gray-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {step === 'details' && (
            <>
              {/* Product Info */}
              <div className="flex items-center gap-3 bg-gray-800 p-3 rounded-lg">
                {request?.product_image_url && (
                  <img
                    src={request.product_image_url}
                    alt={request.product_name}
                    className="w-16 h-16 object-contain rounded-lg bg-gray-700"
                  />
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-white">{request?.product_name}</h4>
                  <p className="text-sm text-gray-400">Qty: {request?.quantity}</p>
                </div>
              </div>

              {/* Price Comparison */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Original Price:</span>
                  <span className="text-gray-500 line-through">${request?.original_price?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-400">Your Bargain Price:</span>
                  <span className="text-2xl font-bold text-green-400">${request?.final_price?.toFixed(2)}</span>
                </div>
                {request?.quantity > 1 && (
                  <div className="mt-2 pt-2 border-t border-gray-700 flex justify-between">
                    <span className="text-sm text-gray-400">Total ({request.quantity} items):</span>
                    <span className="text-sm font-medium text-green-400">
                      ${(request.final_price * request.quantity).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Expiry Warning */}
              {request?.expires_at && (
                <div className="flex items-center gap-2 text-amber-400 bg-amber-900/30 px-3 py-2 rounded-lg">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">Expires: {formatDate(request.expires_at)}</span>
                </div>
              )}

              {/* Reserved for */}
              <div className="bg-gray-800 rounded-lg p-3">
                <p className="text-xs text-gray-500">Reserved for:</p>
                <p className="text-sm font-medium text-white">{request?.customer_name}</p>
              </div>
            </>
          )}

          {step === 'checkout' && (
            <>
              {/* Contact Info */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-300">Contact Information</h5>
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={checkoutForm.fullName}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, fullName: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={checkoutForm.email}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
                <input
                  type="tel"
                  placeholder="Phone *"
                  value={checkoutForm.phone}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>

              {/* Shipping Address */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Shipping Address
                </h5>
                <input
                  type="text"
                  placeholder="Street Address *"
                  value={checkoutForm.streetAddress}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, streetAddress: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="City *"
                  value={checkoutForm.city}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, city: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="State *"
                    value={checkoutForm.state}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, state: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    placeholder="Zip Code *"
                    value={checkoutForm.zipCode}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, zipCode: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Shipping Method */}
              {shippingSettings && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <h5 className="text-sm font-medium text-gray-300 mb-2">Shipping Method</h5>
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm">
                      {shippingSettings.shippingType === 'free' ? 'Free Shipping' : 'Standard Shipping'}
                    </span>
                    <span className="text-green-400 font-medium">
                      {shippingSettings.shippingType === 'free' ? 'FREE' : `$${calculateShipping().toFixed(2)}`}
                    </span>
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Special Instructions (Optional)</label>
                <textarea
                  value={checkoutForm.specialInstructions}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, specialInstructions: e.target.value })}
                  placeholder="Any special delivery instructions..."
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Order Summary */}
              <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                <h5 className="text-sm font-medium text-gray-300">Order Summary</h5>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{request?.product_name} x {request?.quantity}</span>
                  <span className="text-white">${getSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Shipping</span>
                  <span className="text-white">${calculateShipping().toFixed(2)}</span>
                </div>
                {storeSettings?.tax?.enabled && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Tax ({getTaxRate()}%)</span>
                    <span className="text-white">${calculateTax(getSubtotal()).toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-700 pt-2 flex justify-between font-semibold">
                  <span className="text-white">Total</span>
                  <span className="text-green-400">${getTotal().toFixed(2)}</span>
                </div>
              </div>
            </>
          )}

          {step === 'payment' && stripePromise && (
            <>
              <div className="bg-gray-800 rounded-lg p-3 mb-4">
                <div className="flex justify-between font-semibold">
                  <span className="text-white">Total to Pay</span>
                  <span className="text-green-400">${getTotal().toFixed(2)}</span>
                </div>
              </div>
              
              <h5 className="text-sm font-medium text-gray-300 flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4" />
                Payment Details
              </h5>
              
              <Elements stripe={stripePromise}>
                <PaymentForm
                  amount={getTotal()}
                  onPaymentSuccess={handlePaymentSuccess}
                  onCancel={() => setStep('checkout')}
                  customerInfo={checkoutForm}
                  bargainRequest={request}
                />
              </Elements>
            </>
          )}
        </div>

        {/* Sticky Footer */}
        {step === 'details' && (
          <div className="px-6 py-4 border-t border-gray-700 flex-shrink-0 bg-gray-800 rounded-b-2xl">
            <button
              onClick={handleCheckout}
              className="w-full py-3 rounded-lg bg-gray-700 text-white font-bold flex items-center justify-center gap-2 hover:bg-gray-600"
            >
              <ShoppingBag className="h-5 w-5" />
              Checkout
            </button>
          </div>
        )}

        {step === 'checkout' && (
          <div className="px-6 py-4 border-t border-gray-700 flex-shrink-0 bg-gray-800 rounded-b-2xl space-y-2">
            <div className="flex gap-3">
              <button
                onClick={() => setStep('details')}
                className="flex-1 py-3 rounded-lg bg-gray-700 text-white font-medium hover:bg-gray-600"
              >
                Back
              </button>
              <button
                onClick={handleProceedToPayment}
                className="flex-1 py-3 rounded-lg bg-gray-600 text-white font-bold flex items-center justify-center gap-2 hover:bg-gray-500"
              >
                Pay Now - ${getTotal().toFixed(2)}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Validation Error Modal */}
      {validationError && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]">
          <div className="bg-gray-900 rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-red-900 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Required Fields Missing</h4>
                <p className="text-gray-400 text-sm mt-1">{validationError}</p>
              </div>
            </div>
            <button
              onClick={() => setValidationError(null)}
              className="w-full py-2 rounded-lg bg-gray-700 text-white font-medium hover:bg-gray-600"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BargainPurchase;
